import React from 'react';
import {
  getCachedImageUrl,
  releaseCachedImageUrl,
  peekCachedImageUrl,
} from '../utils/imageCache';

/**
 * GIF de 1×1 transparente.
 *
 * Es el `src` mientras la imagen real no está. Con `src` vacío el navegador dibuja el
 * `alt` como texto y, en Chrome, su ícono de "imagen rota" — que es el placeholder feo
 * que se veía al entrar una tarjeta nueva a la grilla
 * (ADR-2026-08-27-la-imagen-que-todavia-no-cargo-no-muestra-el-alt). Con un pixel
 * transparente el navegador no dibuja nada y se ve el fondo gris de abajo, y el `alt`
 * sigue puesto para los lectores de pantalla.
 */
const PIXEL_TRANSPARENTE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

type Props = React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  lazy?: boolean;
};

const CachedImg: React.FC<Props> = ({
  src,
  alt,
  className,
  style,
  onLoad,
  onError,
  loading,
  lazy = true,
  ...rest
}) => {
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  // Init SÍNCRONO desde el caché: si la imagen ya está, se ve al instante (sin
  // pasar por un frame en blanco → sin parpadeo al volver a la pantalla).
  const [displayUrl, setDisplayUrl] = React.useState<string>(
    () => peekCachedImageUrl(src) || '',
  );
  const srcRef = React.useRef<string>('');

  const startFetch = React.useCallback(async () => {
    if (!src) return;
    const currentSrc = src;
    srcRef.current = currentSrc;
    try {
      const { url } = await getCachedImageUrl(currentSrc);
      if (srcRef.current === currentSrc) setDisplayUrl(url);
    } catch {
      if (srcRef.current === currentSrc) setDisplayUrl(currentSrc);
    }
  }, [src]);

  // Al cambiar `src`: si la nueva ya está cacheada, mostrarla YA (sin blanco);
  // si no, blanquear para que entre el placeholder mientras carga. Libera la
  // anterior al desmontar/cambiar (baja el ref; el LRU la mantiene caliente).
  React.useEffect(() => {
    setDisplayUrl(peekCachedImageUrl(src) || '');
    return () => {
      if (srcRef.current) {
        try {
          releaseCachedImageUrl(srcRef.current);
        } catch {}
      }
    };
  }, [src]);

  // Adquiere la imagen (toma el ref + asegura que esté bajada). Si ya está
  // cacheada, corre YA; si no y es lazy, espera a estar cerca del viewport.
  React.useEffect(() => {
    const cached = peekCachedImageUrl(src);
    if (!lazy || cached) {
      void startFetch();
      return;
    }
    const el = imgRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      void startFetch();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.disconnect();
            void startFetch();
          }
        });
      },
      // 600px y no 200: la grilla de productos SACA la tarjeta elegida, así que las de
      // abajo suben de golpe y una que estaba fuera de vista aparece ya en pantalla. Con
      // 200px la bajada arrancaba recién ahí y se veía el recuadro gris; con 600 la
      // imagen ya viene en camino cuando la fila se reacomoda
      // (ADR-2026-08-27-la-imagen-que-todavia-no-cargo-no-muestra-el-alt).
      { rootMargin: '600px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [lazy, startFetch, src]);

  // Fondo gris suave mientras no hay imagen. Va junto con el `PIXEL_TRANSPARENTE` del
  // `src`: el gris es lo que se ve, y el pixel es lo que evita que el navegador dibuje
  // encima el `alt` y su ícono de imagen rota. Se mezcla con el `style` del caller.
  const mergedStyle: React.CSSProperties = displayUrl
    ? style || {}
    : { backgroundColor: '#f3f4f6', ...(style || {}) };

  return (
    <img
      ref={imgRef}
      src={displayUrl || PIXEL_TRANSPARENTE}
      alt={alt}
      className={className}
      style={mergedStyle}
      loading={loading || 'lazy'}
      decoding="async"
      onLoad={onLoad}
      onError={onError}
      {...rest}
    />
  );
};

export default CachedImg;
